import { HttpInterceptor, HttpEvent, HttpRequest, HttpHandler, HttpErrorResponse, HttpResponse } from '@angular/common/http';
import { Router } from '@angular/router';
import { throwError, Observable } from 'rxjs';
import { map, catchError } from 'rxjs/operators';
import { ValidationService } from './validation.service';
import { ProcessService } from './process.service';
import { Injectable } from '@angular/core';
import * as pagesConfig from '../../../config/pages-config';
import { AUTHENTICATION_ERROR_CODE, AUTHENTICATION_WHITELIST, MTAN_STATIC_VALUES, PAYMENT_URL } from './../../common/constants/Defines';
import { CustomErrorHandlerService } from '@vfde-fe/ngx-lib/services/custom-error-handler';
import { environment } from '../../../environments/environment';
import { SpinnerLoader, SpinnerService } from '@vfde-fe/ngx-lib/components/spinner';
import { API_URLS } from '../../common/constants/routes-config';
import { CoreAuthenticationService } from '@vfde-fe/ngx-lib/services/core-authentication';
import { RoutesUtilsService } from '@vfde-fe/ngx-lib/services/routes-utils';
import { Helper } from '@vfde-fe/ngx-lib/services/helper-utils';
import { HEADER_CONTEXT_WHITE_LIST } from '@vfde-fe/ngx-lib/pipes/enterprise-translate';

/**
 * HTTPClientInterceptor
 */
 @Injectable({
    providedIn: 'root',
})
export class HTTPClientInterceptor implements HttpInterceptor {

    private pagesConfig;
    public constructor (
        private router: Router,
        private processService: ProcessService,
        private validationService: ValidationService,
        private customErrorHandlerService: CustomErrorHandlerService,
        private spinnerService: SpinnerService,
        private authenticationService: CoreAuthenticationService,
        private routesUtilsService: RoutesUtilsService,
        private helper: Helper) {
        this.pagesConfig = pagesConfig.config;
    }

    /**
     * intercept
     * @param httpRequest
     * @param next
     * @returns
     */
    public intercept (request: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {

        const data = request.url.startsWith('{') ? JSON.parse(request.url) : null;
        request = request.clone({ url: data ? data.url : request.url });

        if (request.url.indexOf(environment.baseUrlNil) !== -1) {

            request = request.clone({
                setHeaders: {
                    'x-vf-api': (new Date().valueOf()).toString(),
                    'x-vf-clientID': 'MyVFWeb',
                },
            });
        }
        if (HEADER_CONTEXT_WHITE_LIST.CONTEXT.indexOf(this.helper.getContext()) > -1
            && this.helper.detectIsUrlInWhiteList(request.url, HEADER_CONTEXT_WHITE_LIST.URLS)) {
            request = request.clone({
                setHeaders: {
                    'X-USE-CONTEXT': this.helper.getContext(),
                },
            });

        }

        const disableSpinner = request.headers.get('disableSpinner') === 'true';
        const loader: SpinnerLoader = !disableSpinner ? this.spinnerService.startLoader() : null;

        // Check if url is one of the defined process urls to apply generic routing
        return next.handle(request).pipe(map((event: HttpEvent<unknown>) => {
            if (event instanceof HttpResponse) {
                if (loader) {
                    this.spinnerService.endLoader(loader, true);
                }
                if (this.processService.checkIfProcessUri(request.url) && !request.url.includes(API_URLS.PROCESS.RESEND_MTAN)) {
                    // Successful Response;
                    return this.processSuccessResponse(event);
                }
            }

            return event;

        }), catchError((error: HttpErrorResponse) => {
            if (error instanceof HttpErrorResponse) {
                if (loader) {
                    this.spinnerService.endLoader(loader, false);
                }
            }

            return this.processErrorResponse(error, request);
        }),
        );
    }

    /** processSuccessResponse */
    public processSuccessResponse (response) {
        const result = response;
        if (result.body.stepName) {
            const route = this.processService.handleGenericRouting(result.body.stepName);
            if (route) {
                this.validationService.addStepAttributes(result.body.parameters, result.body.stepName);
                if (this.processService.navigate) {
                    this.router.navigate([route]);

                }

                return response;
            } else {
                console.warn('Step name retrieved does not match to any route');

                return response;
            }
        } else if (result.body.lastStep) {
            return response;
        } else {
            return throwError('Next step not retrieved from BE');
        }
    }

    /**
     * processErrorResponse to handle generic error on level of application (404,500,401,403,etc..)
     * @param {HttpErrorResponse} response
     */
    public processErrorResponse (response: HttpErrorResponse, request?: HttpRequest<unknown>) {
        if (response instanceof HttpErrorResponse) {
            let errorCode = null;
            response = this.formatOperationError(response);
            try {
                if (response.url.indexOf(environment.baseUrlVluxgate) !== -1) {
                    errorCode = response.error.operationError ? response.error.operationError.code : null;
                } else if (response.url.indexOf(environment.baseUrlNil) !== -1) {
                    errorCode = response.error.faultMessage ? response.error.faultMessage.code : null;
                } else {
                    errorCode = response.error.operationError ? response.error.operationError.code : null;
                }
            } catch (e) {
                // console.log(e);
                // do nothing and not to stop code sequence in case response in not in JSON format
                return throwError(response);
            }
            if (response.url.indexOf(environment.cmsConfigPath) > -1 || response.url.indexOf(environment.cmsErrorMessagesPath) > -1 || response.url.indexOf(environment.cmsCommonWording) > -1 || response.url.indexOf(environment.configPath) > -1) {
                return throwError(response);
            }
            switch (response.status) {
                case 401:{
                    // redirect to login page
                    let foundWhiteList = false;
                    if (errorCode === AUTHENTICATION_ERROR_CODE.MINT_AUTHENTICATION_REQUIRED) {
                        AUTHENTICATION_WHITELIST.forEach((item) => {
                            if (this.router.url.indexOf(this.pagesConfig[item].route) > -1) {
                                foundWhiteList = true;
                            }
                        });
                        if (foundWhiteList) {
                            return throwError(response);
                        } else {
                            this.authenticationService.logout();
                            this.router.navigate([this.pagesConfig.login.route]);

                            return throwError(response);
                        }
                    } else if (errorCode === AUTHENTICATION_ERROR_CODE.NIL_AUTHENTICATION_REQUIRED) {

                        window.location.href = API_URLS.LOGOUT.LOGOUT + '?targetURL=/' + PAYMENT_URL + pagesConfig.config.login.route + '?targetURL=' + encodeURIComponent(encodeURIComponent(window.location.href));

                        return throwError(response);
                    }

                    return throwError(response);
                }
                // case 403:
                //   if(errorCode === AUTHORIZATION_ERROR_CODE.SECURITY_CHALLENGE_FAILED ){
                //       let queryParam:any = {
                //           accessLevel: this.processService.getStepUsingName(response.url).access.accessLevel
                //       };
                //       if(this.authenticationService.isAuthenticated()){
                //           queryParam.contractType = this.authenticationService.getCurrentUserModel().getActor();
                //       }
                //       console.log('QueryParam (403):',queryParam);
                //       this.router.navigate([pagesConfig.config.unauthorized.name], {
                //           queryParams: queryParam
                //       });
                //       return;
                //   } else {
                //       return Observable.throw(response);
                //   }
                default: {
                    if (response.status === 403 && errorCode === MTAN_STATIC_VALUES.RESEND_MAXED_OUT) {
                        return throwError(response);
                    }
                    const currentModule = this.processService.getCurrentModule() !== '' ? this.processService.getCurrentModule() : undefined;
                    const currentUsecase = this.routesUtilsService.getCurrentUsecase() !== '' ? this.routesUtilsService.getCurrentUsecase() : undefined;
                    const failureTrackng = (request && request.headers) ? request.headers.get('stopFailureTracking') : undefined === 'true';
                    const locationTracking = !failureTrackng ? request.headers.get('locationTracking') : undefined;
                    if (failureTrackng) {
                        return throwError(response);
                    }
                    if (!errorCode) {
                        this.customErrorHandlerService.getErrorDescription(currentModule, undefined, response.status.toString(), true, false, false, currentUsecase, undefined, locationTracking);

                        return throwError(response);
                    } else {
                        this.customErrorHandlerService.getErrorDescription(currentModule, errorCode, response.status.toString(), true, false, false, currentUsecase, undefined, locationTracking);

                        return throwError(response);
                    }
                }
            }
        }
    }

    /** format operationError */
    public formatOperationError (error) {
        if (error._body) {
            error._body = JSON.parse(error._body);
            Array.isArray(error._body.operationError) ? error._body.operationError = error._body.operationError[0] : '';
            error._body = JSON.stringify(error._body);
        }

        return error;

    }
}
